import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { QCProgressEvent, QCRun, QCStreamCallbacks } from "../types/qcTypes";
import { getApiBaseUrl } from "../../../config/runtimeConfig";
import { partialSectionFromEvent } from "../utils/qcPartialSections";

interface UseQualityCheckReturn {
  // State
  loading: boolean;
  error: string | null;

  // Start QC
  startDocumentQC: (
    documentId: number,
    workspaceId: string,
    sectionIds: number[] | null,
    mode: "integrated" | "quick_action",
  ) => Promise<{ qc_run_id: number }>;

  startIndependentQC: (
    formData: FormData,
    workspaceId: string,
  ) => Promise<{ qc_run_id: number }>;

  // Stream progress
  connectToStream: (
    qcRunId: number,
    token: string,
    workspaceId: string,
    callbacks: QCStreamCallbacks,
  ) => () => void; // returns cleanup function

  // Get results
  getQCRun: (qcRunId: number, workspaceId: string) => Promise<QCRun>;

  // Resolve findings
  acceptFinding: (
    qcRunId: number,
    findingId: number,
    workspaceId: string,
    // When provided, the user-edited replacement is persisted on the finding
    // and used at apply time instead of the AI's SuggestedText.
    editedText?: string | null,
  ) => Promise<void>;
  declineFinding: (
    qcRunId: number,
    findingId: number,
    workspaceId: string,
  ) => Promise<void>;
  revertFinding: (
    qcRunId: number,
    findingId: number,
    workspaceId: string,
  ) => Promise<void>;
  bulkResolve: (
    qcRunId: number,
    resolution: "accepted" | "declined" | null,
    workspaceId: string,
    sectionIndex?: number | null,
  ) => Promise<void>;

  // Apply changes
  applyChanges: (
    qcRunId: number,
    workspaceId: string,
    sectionIndex?: number | null,
  ) => Promise<{ sections_updated: number }>;

  // Suggestion feedback ("Did this help?")
  submitFeedback: (
    qcRunId: number,
    findingId: number,
    helpful: boolean,
    workspaceId: string,
  ) => Promise<void>;

  // Download tracked-changes document
  downloadTrackedDocument: (
    qcRunId: number,
    workspaceId: string,
  ) => Promise<void>;
  downloadLoading: boolean;

  // Drop the SSE stream only. The run carries on server-side, so a caller can
  // reconnect to it later.
  disconnect: () => void;

  // Stop following the run for good (modal close, dismiss, Start over, Retry):
  // drop the stream and ask the backend to cancel the run if it has not
  // started checking sections -- otherwise it would keep extracting uploads
  // nobody is waiting for, holding the shared extraction slot. A run that is
  // already checking sections is left to finish (its results are kept).
  cancel: () => void;

  // Explicit Cancel: drop the stream and stop the run even if it is already
  // checking sections. Sections already checked keep their findings.
  stop: () => void;
}

// When a 409 asks us to wait but its Retry-After can't be read, wait this long.
// A cross-origin page only sees Retry-After if the API exposes it via CORS; on
// SSI test it didn't (QC run 48, 2026-10-21) and the old 2 s fallback produced a
// reconnect every 2 s for minutes while the server asked for 60-90 s.
export const LEASE_RETRY_FALLBACK_SECONDS = 10;
// Never wait longer than this between attempts; the lease-conflict deadline
// still bounds the total.
export const LEASE_RETRY_MAX_SECONDS = 120;

/**
 * Seconds to wait before retrying a 409, from its Retry-After header.
 *
 * Accepts delta-seconds or an HTTP date. Missing, unreadable or non-positive
 * values fall back to LEASE_RETRY_FALLBACK_SECONDS; large ones are capped.
 */
export function leaseRetryDelaySeconds(
  header: string | null | undefined,
  now: number = Date.now(),
): number {
  const value = (header ?? "").trim();
  if (value) {
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(seconds, LEASE_RETRY_MAX_SECONDS);
    }
    const date = Date.parse(value);
    if (!Number.isNaN(date) && date > now) {
      return Math.min(Math.ceil((date - now) / 1000), LEASE_RETRY_MAX_SECONDS);
    }
  }
  return LEASE_RETRY_FALLBACK_SECONDS;
}

const useQualityCheck = (): UseQualityCheckReturn => {
  const authFetch = useAuthFetch();
  const rawFetch = useAuthFetch("raw");
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  // The run this hook follows, for cancel(). The stream's token is kept so the
  // cancel request can be sent synchronously while the page is unloading,
  // when there is no time to fetch a fresh one.
  const activeRunRef = useRef<{
    qcRunId: number;
    workspaceId: string;
    token: string;
    // The waiter ticket from the stream's "waiter" event. The backend cancels
    // only for the ticket that is still current, so a window that another
    // window (or its own reconnect) has superseded cannot cancel that run.
    ticket?: string;
  } | null>(null);

  const withWorkspace = useCallback((endpoint: string, workspaceId: string) => {
    const separator = endpoint.includes("?") ? "&" : "?";
    return `${endpoint}${separator}workspace_id=${encodeURIComponent(workspaceId)}`;
  }, []);

  // Ask the backend to cancel the followed run. Fire-and-forget: keepalive
  // lets it outlive the page, and failures are ignored -- a 409 means the run
  // already started or finished, which is exactly when there is nothing to do.
  // stopRunning (an explicit Cancel only) also stops a run that is already
  // checking sections; leaving the page never does, so no work is lost.
  const sendCancel = useCallback((stopRunning: boolean) => {
    const run = activeRunRef.current;
    activeRunRef.current = null;
    if (!run) return;
    const ticket = run.ticket
      ? `&ticket=${encodeURIComponent(run.ticket)}`
      : "";
    const stop = stopRunning ? "&stop=running" : "";
    const url = `${getApiBaseUrl()}/api/workspace/meridian/qc/qc/${run.qcRunId}/cancel?workspace_id=${encodeURIComponent(run.workspaceId)}${ticket}${stop}`;
    try {
      void fetch(url, {
        method: "POST",
        keepalive: true,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${run.token}`,
          "X-Workspace-Id": run.workspaceId,
        },
      }).catch(() => undefined);
    } catch {
      // fetch can throw synchronously during unload; nothing more to do.
    }
  }, []);

  // Leaving the page (tab close, reload, navigation away from the app) gives
  // no chance to click anything, so treat it as closing the run: one not yet
  // checking sections is cancelled, one already checking carries on.
  useEffect(() => {
    const leavePage = () => sendCancel(false);
    window.addEventListener("pagehide", leavePage);
    return () => window.removeEventListener("pagehide", leavePage);
  }, [sendCancel]);

  // Self-cleanup on unmount: the component following the run is gone, so
  // nothing will execute a run that has not started -- cancel it rather than
  // let it finish extracting for no one.
  useEffect(() => {
    return () => {
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
        streamAbortRef.current = null;
      }
      sendCancel(false);
    };
  }, [sendCancel]);

  // Drop the stream only; the run is left as it is.
  const disconnect = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    disconnect();
    sendCancel(false);
  }, [disconnect, sendCancel]);

  const stop = useCallback(() => {
    disconnect();
    sendCancel(true);
  }, [disconnect, sendCancel]);

  const startDocumentQC = useCallback(
    async (
      documentId: number,
      workspaceId: string,
      sectionIds: number[] | null,
      mode: "integrated" | "quick_action",
    ) => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `/api/workspace/meridian/qc/documents/${documentId}/qc?workspace_id=${workspaceId}`,
          "start document QC",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ mode, section_ids: sectionIds }),
          },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to start QC";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const startIndependentQC = useCallback(
    async (formData: FormData, workspaceId: string) => {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(
          `/api/workspace/meridian/qc/qc/check?workspace_id=${workspaceId}`,
          "start independent QC",
          {
            method: "POST",
            body: formData, // multipart, no Content-Type header
          },
          120000, // 2 min timeout for large file uploads
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to start QC";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const connectToStream = useCallback(
    (
      qcRunId: number,
      token: string,
      workspaceId: string,
      callbacks: QCStreamCallbacks,
    ): (() => void) => {
      const apiUrl = getApiBaseUrl();
      const url = `${apiUrl}/api/workspace/meridian/qc/qc/${qcRunId}/stream?workspace_id=${encodeURIComponent(workspaceId)}`;
      const authHeaders = {
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
        "X-Workspace-Id": workspaceId,
      };
      activeRunRef.current = { qcRunId, workspaceId, token };
      // The server has said the run is over (or that another connection now
      // follows it): nothing left for cancel() to do. A client-side give-up
      // does not count -- the run may still be extracting server-side.
      const stopTracking = (): void => {
        if (activeRunRef.current?.qcRunId === qcRunId) {
          activeRunRef.current = null;
        }
      };

      // SSE stream resilience (originally Task #491).
      //
      // A dropped stream IS now resumable at the SSE layer: the backend holds
      // QCContext until the run is terminal and records per-section progress,
      // so reconnecting picks up where the last executor stopped rather than
      // restarting.
      //
      // On disconnect we still (a) poll GET /qc/{id} once, since the run may
      // have finished server-side while we were away, and (b) if it is still
      // going, retry the authenticated fetch stream with bounded backoff.
      // Sized to outlast a server-side execution lease (90s). A process restart
      // leaves the run's lease stale for up to that long, and the old limit of
      // 3 gave up after ~12s -- before the backend could possibly hand the run
      // back. 8 attempts of linear backoff is ~72s, which combined with the
      // lease-conflict path below covers a restart.
      const MAX_RECONNECT_ATTEMPTS = 8;
      const RECONNECT_DELAY_MS = 2000;

      // A 409 means the lease is held, not that anything is wrong, so those
      // retries are bounded by elapsed time rather than by attempts -- but
      // bounded all the same, so a permanently wedged run cannot spin forever.
      //
      // The clock starts at the first conflict, not when the stream opens. A QC
      // run can easily outlast this window; anchoring it to connect() meant a
      // drop late in a long run found the budget already spent and reported the
      // run stuck on its very first retry.
      const LEASE_CONFLICT_TIMEOUT_MS = 300_000;
      let leaseConflictDeadline: number | null = null;

      let reconnectAttempts = 0;
      let streamCompleted = false;
      let aborted = false;

      const pollRunStatus = async (): Promise<QCRun | null> => {
        try {
          const response: Response = await fetch(
            `${apiUrl}/api/workspace/meridian/qc/qc/${qcRunId}?workspace_id=${encodeURIComponent(workspaceId)}`,
            {
              headers: authHeaders,
              credentials: "include",
            },
          );
          if (!response.ok) return null;
          return (await response.json()) as QCRun;
        } catch {
          return null;
        }
      };

      const reconstructFromRun = (run: QCRun): void => {
        stopTracking();
        callbacks.onRecoveredViaPoll?.(run);
        callbacks.onComplete(run.TotalFindings, run.TotalSections);
      };

      const handleStreamEvent = (
        data: QCProgressEvent,
        controller: AbortController,
      ): void => {
        switch (data.type) {
          case "waiter":
            // Each (re)connect gets a new ticket; keep the latest.
            if (data.ticket && activeRunRef.current?.qcRunId === qcRunId) {
              activeRunRef.current.ticket = data.ticket;
            }
            break;
          case "qc_started":
            callbacks.onStarted(data.total_sections!);
            break;
          case "section_start":
            callbacks.onSectionStart(
              data.section_index!,
              data.section_title!,
              data.section_content!,
              data.section_id,
            );
            break;
          case "finding":
            callbacks.onFinding(data.finding!);
            break;
          case "section_complete":
            callbacks.onSectionComplete(
              data.section_index!,
              data.finding_count!,
            );
            break;
          case "section_error":
            callbacks.onSectionError(
              data.section_index!,
              data.finding_count!,
              data.error!,
              {
                sectionTitle: data.section_title,
                sectionId: data.section_id,
                partial: partialSectionFromEvent(data),
              },
            );
            break;
          case "complete":
            streamCompleted = true;
            stopTracking();
            callbacks.onComplete(data.total_findings!, data.total_sections!);
            controller.abort();
            break;
          case "error":
            streamCompleted = true;
            stopTracking();
            callbacks.onError(data.error!, {
              sectionIndex: data.section_index,
              sectionTitle: data.section_title,
              sectionId: data.section_id,
            });
            controller.abort();
            break;
        }
        // Reset backoff on any successfully parsed message so a brief blip
        // does not burn the reconnect budget for the rest of the run.
        reconnectAttempts = 0;
      };

      let connect: () => Promise<void>;

      const recoverOrReconnect = async (): Promise<void> => {
        if (streamCompleted || aborted) return;

        const run = await pollRunStatus();
        if (aborted) return;
        if (run) {
          if (run.Status === "completed") {
            streamCompleted = true;
            reconstructFromRun(run);
            return;
          }
          if (run.Status === "failed" || run.Status === "cancelled") {
            streamCompleted = true;
            stopTracking();
            callbacks.onError(
              run.ErrorMessage ||
                `Quality check ${run.Status} during streaming`,
            );
            return;
          }
          // "pending" / "running" -- fall through to reconnect.
        }

        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          callbacks.onReconnecting?.(reconnectAttempts, MAX_RECONNECT_ATTEMPTS);
          const delay = RECONNECT_DELAY_MS * reconnectAttempts;
          setTimeout(() => {
            if (!aborted) void connect();
          }, delay);
        } else {
          callbacks.onError(
            "Connection lost during quality check. Click Retry to start over.",
          );
        }
      };

      connect = async (): Promise<void> => {
        const controller = new AbortController();
        streamAbortRef.current = controller;

        try {
          const response = await fetch(url, {
            method: "GET",
            headers: authHeaders,
            credentials: "include",
            signal: controller.signal,
          });
          // 409 means another executor still holds this run's lease -- usually
          // the previous stream has not finished unwinding. That is transient,
          // not a failed run, so honour the server's Retry-After and try again
          // without burning a reconnect attempt.
          if (response.status === 409) {
            if (leaseConflictDeadline === null) {
              leaseConflictDeadline = Date.now() + LEASE_CONFLICT_TIMEOUT_MS;
            }
            if (Date.now() >= leaseConflictDeadline) {
              callbacks.onError(
                "This quality check is still running, but its progress can't " +
                  "be shown right now. Reopen it in a few minutes to see the results.",
              );
              return;
            }
            const retryAfter = leaseRetryDelaySeconds(
              response.headers.get("Retry-After"),
            );
            console.info(
              `QC run ${qcRunId} still held by another stream; retrying in ${retryAfter}s`,
            );
            setTimeout(() => {
              if (!aborted && !streamCompleted) void connect();
            }, retryAfter * 1000);
            return;
          }
          if (!response.ok || !response.body) {
            throw new Error(`HTTP ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (!aborted && !streamCompleted) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
              if (!line.startsWith("data:")) continue;
              const payload = line.slice(5).replace(/^ /, "").trim();
              if (!payload || payload === "[DONE]") continue;
              try {
                handleStreamEvent(
                  JSON.parse(payload) as QCProgressEvent,
                  controller,
                );
              } catch (err) {
                console.error("Failed to parse QC SSE event:", err);
              }
              if (streamCompleted) break;
            }
          }
        } catch (err) {
          if (!aborted && !streamCompleted && !controller.signal.aborted) {
            console.warn("QC SSE fetch stream disconnected:", err);
          }
        } finally {
          if (streamAbortRef.current === controller) {
            streamAbortRef.current = null;
          }
        }

        if (!aborted && !streamCompleted && !controller.signal.aborted) {
          await recoverOrReconnect();
        }
      };

      if (streamAbortRef.current) streamAbortRef.current.abort();

      void connect();

      return () => {
        aborted = true;
        if (streamAbortRef.current) {
          streamAbortRef.current.abort();
          streamAbortRef.current = null;
        }
      };
    },
    [],
  );

  const getQCRun = useCallback(
    async (qcRunId: number, workspaceId: string): Promise<QCRun> => {
      const response = await authFetch(
        withWorkspace(`/api/workspace/meridian/qc/qc/${qcRunId}`, workspaceId),
        "get QC run",
      );
      return response;
    },
    [authFetch, withWorkspace],
  );

  const acceptFinding = useCallback(
    async (
      qcRunId: number,
      findingId: number,
      workspaceId: string,
      editedText?: string | null,
    ) => {
      const body: { resolution: "accepted"; edited_replacement?: string } = {
        resolution: "accepted",
      };
      if (editedText != null) body.edited_replacement = editedText;
      await authFetch(
        withWorkspace(
          `/api/workspace/meridian/qc/qc/${qcRunId}/findings/${findingId}`,
          workspaceId,
        ),
        "accept finding",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
    [authFetch, withWorkspace],
  );

  const declineFinding = useCallback(
    async (qcRunId: number, findingId: number, workspaceId: string) => {
      await authFetch(
        withWorkspace(
          `/api/workspace/meridian/qc/qc/${qcRunId}/findings/${findingId}`,
          workspaceId,
        ),
        "decline finding",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resolution: "declined" }),
        },
      );
    },
    [authFetch, withWorkspace],
  );

  const revertFinding = useCallback(
    async (qcRunId: number, findingId: number, workspaceId: string) => {
      await authFetch(
        withWorkspace(
          `/api/workspace/meridian/qc/qc/${qcRunId}/findings/${findingId}`,
          workspaceId,
        ),
        "revert finding",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ resolution: null }),
        },
      );
    },
    [authFetch, withWorkspace],
  );

  const bulkResolve = useCallback(
    async (
      qcRunId: number,
      resolution: "accepted" | "declined" | null,
      workspaceId: string,
      sectionIndex?: number | null,
    ) => {
      await authFetch(
        withWorkspace(
          `/api/workspace/meridian/qc/qc/${qcRunId}/findings/bulk`,
          workspaceId,
        ),
        "bulk resolve findings",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            resolution,
            section_index: sectionIndex ?? null,
          }),
        },
      );
    },
    [authFetch, withWorkspace],
  );

  const applyChanges = useCallback(
    async (
      qcRunId: number,
      workspaceId: string,
      sectionIndex?: number | null,
    ) => {
      setLoading(true);
      try {
        const endpoint =
          sectionIndex === undefined || sectionIndex === null
            ? `/api/workspace/meridian/qc/qc/${qcRunId}/apply`
            : `/api/workspace/meridian/qc/qc/${qcRunId}/apply?section_index=${encodeURIComponent(sectionIndex)}`;
        const response = await authFetch(
          withWorkspace(endpoint, workspaceId),
          "apply QC changes",
          { method: "POST" },
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to apply changes";
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, withWorkspace],
  );

  const submitFeedback = useCallback(
    async (
      qcRunId: number,
      findingId: number,
      helpful: boolean,
      workspaceId: string,
    ) => {
      await authFetch(
        withWorkspace(
          `/api/workspace/meridian/qc/qc/${qcRunId}/findings/${findingId}/feedback`,
          workspaceId,
        ),
        "submit finding feedback",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ helpful }),
        },
      );
    },
    [authFetch, withWorkspace],
  );

  const downloadTrackedDocument = useCallback(
    async (qcRunId: number, workspaceId: string) => {
      setDownloadLoading(true);
      setError(null);
      try {
        const response: Response = await rawFetch(
          `/api/workspace/meridian/qc/qc/${qcRunId}/download-tracked?workspace_id=${encodeURIComponent(workspaceId)}`,
          "download tracked document",
          {},
          120000,
        );

        const blob = await response.blob();
        const disposition = response.headers.get("Content-Disposition");
        let filename = "document_reviewed.docx";
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }

        // Trigger download via temporary anchor element
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to download tracked document";
        setError(message);
        throw err;
      } finally {
        setDownloadLoading(false);
      }
    },
    [rawFetch],
  );

  return {
    loading,
    error,
    startDocumentQC,
    startIndependentQC,
    connectToStream,
    getQCRun,
    acceptFinding,
    declineFinding,
    revertFinding,
    bulkResolve,
    applyChanges,
    submitFeedback,
    downloadTrackedDocument,
    downloadLoading,
    disconnect,
    stop,
    cancel,
  };
};

export default useQualityCheck;
